import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function CompanySelector() {
  const { selectedCompanyId, setSelectedCompanyId } = useCompany();
  const { data: companies, isLoading, refetch } = trpc.company.list.useQuery();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");

  const createMutation = trpc.company.create.useMutation({
    onSuccess: newCompany => {
      toast.success("Company added successfully");
      setIsAddDialogOpen(false);
      setNewCompanyName("");
      refetch();
      setSelectedCompanyId(newCompany.id);
    },
    onError: error => {
      toast.error("Failed to add company", {
        description: error.message,
      });
    },
  });

  const handleCreateCompany = () => {
    if (!newCompanyName.trim()) {
      toast.error("Please enter a company name");
      return;
    }
    createMutation.mutate({ name: newCompanyName });
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-md">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading companies...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Building2 className="h-4 w-4 text-muted-foreground" />
      <Select
        value={selectedCompanyId?.toString()}
        onValueChange={value => setSelectedCompanyId(parseInt(value))}
      >
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder="Select company" />
        </SelectTrigger>
        <SelectContent>
          {companies?.map(company => (
            <SelectItem key={company.id} value={company.id.toString()}>
              {company.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Add Company
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Company</DialogTitle>
            <DialogDescription>
              Create a new company profile to manage its tax data separately
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                value={newCompanyName}
                onChange={e => setNewCompanyName(e.target.value)}
                placeholder="e.g., Acme GmbH"
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    handleCreateCompany();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddDialogOpen(false);
                setNewCompanyName("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCompany}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Company"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
